import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Box, 
  LinearProgress, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField
} from '@material-ui/core';
import { 
  CloudUpload, 
  CheckCircle, 
  Error, 
  Refresh, 
  Download,
  Storage
} from '@material-ui/icons';
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/storage';
import swal from 'sweetalert';
import AdvancedImageOptimizer from '../../services/AdvancedImageOptimizer';
import ShortUrlService from '../../services/ShortUrlService';

const MigrationTool = () => {
  const [examCode, setExamCode] = useState('');
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({
    current: 0,
    total: 0,
    currentExam: '',
    currentImage: ''
  });
  const [migrationResults, setMigrationResults] = useState([]);

  // Initialize services
  useEffect(() => {
    const firebaseConfig = firebase.app().options;
    ShortUrlService.autoInit(firebaseConfig);
  }, []);

  // Scan for specific exam with base64 images
  const scanExam = async () => {
    if (!examCode.trim()) {
      swal("Error", "Please enter an exam code.", "error");
      return;
    }
    
    setLoading(true);
    try {
      const examRef = firebase.database().ref(`TestPapers/${examCode.trim()}`);
      const snapshot = await examRef.once('value');
      const examData = snapshot.val();
      
      if (!examData) {
        swal("Exam Not Found", "No exam found with the provided code.", "error");
        setLoading(false);
        return;
      }
      
      let hasBase64Images = false;
      let base64Count = 0;
      let totalImages = 0;
      let base64Images = [];
      
      if (examData.sections) {
        examData.sections.forEach((section, sIdx) => {
          const questions = section.q || section.questions || [];
          questions.forEach((question, qIdx) => {
            if (question.imageUrl) {
              totalImages++;
              if (question.imageUrl.startsWith('data:image')) {
                hasBase64Images = true;
                base64Count++;
                base64Images.push({
                  section: section.t || section.title || `Section ${sIdx + 1}`,
                  question: qIdx + 1,
                  questionId: question.id,
                  imageUrl: question.imageUrl.substring(0, 100) + '...' // Truncate for display
                });
              }
            }
          });
        });
      }
      
      if (!hasBase64Images) {
        swal("No Base64 Images", "This exam doesn't contain any base64 images to migrate.", "info");
        setLoading(false);
        return;
      }
      
      setExam({
        examCode: examCode.trim(),
        title: examData.title || examData.examTitle || 'Untitled Exam',
        base64Count,
        totalImages,
        base64Images,
        examData
      });
      
      swal("Base64 Images Found", `Found ${base64Count} base64 images in this exam to migrate.`, "info");
      
    } catch (error) {
      console.error('Error scanning exam:', error);
      swal("Error", "Failed to scan exam. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };



  // Migrate the exam
  const migrateExam = async () => {
    if (!exam) {
      swal("No Exam", "No exam to migrate.", "warning");
      return;
    }
    
    setMigrating(true);
    setMigrationProgress({
      current: 0,
      total: exam.base64Count,
      currentExam: exam.examCode,
      currentImage: ''
    });
    setMigrationResults([]);
    
    const { examCode, examData } = exam;
    const migratedExam = JSON.parse(JSON.stringify(examData));
    let migratedCount = 0;
    let errorCount = 0;
    
    try {
      // Process each section and question
      if (migratedExam.sections) {
        for (let sectionIndex = 0; sectionIndex < migratedExam.sections.length; sectionIndex++) {
          const section = migratedExam.sections[sectionIndex];
          const questions = section.q || section.questions || [];
          
          for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
            const question = questions[questionIndex];
            
            if (question.imageUrl && question.imageUrl.startsWith('data:image')) {
              try {
                // Update progress
                setMigrationProgress(prev => ({
                  ...prev,
                  current: migratedCount + 1,
                  currentImage: `Migrating image for question ${questionIndex + 1} in section ${sectionIndex + 1}`
                }));
                
                // Convert base64 to blob without using fetch
                const base64Data = question.imageUrl;
                const byteCharacters = atob(base64Data.split(',')[1]);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                // Determine file type from base64 data
                const mimeType = base64Data.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
                const fileExtension = mimeType.split('/')[1] || 'jpg';
                
                // Create a file object
                const file = new File([byteArray], `question_${question.id}.${fileExtension}`, { type: mimeType });
                
                // Upload optimized image to Firebase Storage
                const result = await AdvancedImageOptimizer.uploadOptimizedImage(file, question.id);
                
                // Convert to short URL
                const shortUrl = ShortUrlService.toShortUrl(result.url);
                
                // Update the question with the new short URL
                questions[questionIndex].imageUrl = shortUrl;
                migratedCount++;
                
                console.log(`✅ Migrated image for question ${question.id} in exam ${examCode}`);
                
              } catch (error) {
                console.error(`❌ Failed to migrate image for question ${question.id} in exam ${examCode}:`, error);
                errorCount++;
              }
            }
          }
        }
      }
      
      // Save the migrated exam back to Firebase
      const examRef = firebase.database().ref(`TestPapers/${examCode}`);
      await examRef.set(migratedExam);
      
      const result = {
        examCode,
        success: true,
        migratedCount,
        errorCount,
        message: `Successfully migrated ${migratedCount} images${errorCount > 0 ? ` (${errorCount} errors)` : ''}`
      };
      
      setMigrationResults([result]);
      
      swal("Migration Complete", 
        `Successfully migrated ${migratedCount} images${errorCount > 0 ? ` (${errorCount} errors)` : ''}`, 
        "success"
      );
      
    } catch (error) {
      console.error(`❌ Failed to migrate exam ${examCode}:`, error);
      const result = {
        examCode,
        success: false,
        migratedCount: 0,
        errorCount: 0,
        message: `Failed to migrate: ${error.message}`
      };
      setMigrationResults([result]);
      swal("Migration Failed", `Failed to migrate exam: ${error.message}`, "error");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <Container maxWidth="lg" style={{ marginTop: 20, marginBottom: 20 }}>
      <Paper style={{ padding: 24 }}>
        <Typography variant="h4" gutterBottom style={{ color: '#1976d2', fontWeight: 700 }}>
          <Storage style={{ marginRight: 12, verticalAlign: 'middle' }} />
          Base64 to Firebase Storage Migration Tool
        </Typography>
        
        <Typography variant="body1" style={{ marginBottom: 24, color: '#666' }}>
          Convert old exams with base64 images to Firebase Storage with short URLs for cost optimization.
        </Typography>

        <Box style={{ marginBottom: 24 }}>
          <TextField
            label="Exam Code"
            variant="outlined"
            value={examCode}
            onChange={(e) => setExamCode(e.target.value)}
            placeholder="Enter exam code to migrate"
            style={{ marginRight: 16, minWidth: 200 }}
            disabled={loading || migrating}
          />
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={scanExam}
            disabled={loading || migrating || !examCode.trim()}
            style={{ marginRight: 12 }}
          >
            {loading ? 'Scanning...' : 'Scan Exam'}
          </Button>
          
          {exam && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloudUpload />}
              onClick={migrateExam}
              disabled={migrating}
            >
              {migrating ? 'Migrating...' : `Migrate ${exam.base64Count} Images`}
            </Button>
          )}
        </Box>

        {/* Migration Progress */}
        {migrating && (
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h6" gutterBottom>
              Migration Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(migrationProgress.current / migrationProgress.total) * 100}
              style={{ marginBottom: 8 }}
            />
            <Typography variant="body2" color="textSecondary">
              {migrationProgress.current} of {migrationProgress.total} exams
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {migrationProgress.currentExam}
            </Typography>
            {migrationProgress.currentImage && (
              <Typography variant="body2" color="textSecondary">
                {migrationProgress.currentImage}
              </Typography>
            )}
          </Box>
        )}

        {/* Summary */}
        {exam && (
          <Box style={{ marginBottom: 24 }}>
            <Paper style={{ padding: 16, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
              <Typography variant="h6" style={{ color: '#1976d2', marginBottom: 8 }}>
                Found {exam.base64Count} base64 images in "{exam.title}"
              </Typography>
              <Typography variant="body2" style={{ color: '#1976d2' }}>
                Estimated cost savings: ~{(exam.base64Count * 0.1).toFixed(1)} MB of Firebase Realtime Database storage
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Exam Details */}
        {exam && (
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h6" gutterBottom>
              Base64 Images Found
            </Typography>
            <List>
              {exam.base64Images.map((image, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={`Question ${image.question} in ${image.section}`}
                    secondary={`Question ID: ${image.questionId}`}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label="Base64"
                      color="secondary"
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Migration Results */}
        {migrationResults.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Migration Results
            </Typography>
            <List>
              {migrationResults.map((result, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={result.examCode}
                    secondary={result.message}
                  />
                  <ListItemSecondaryAction>
                    {result.success ? (
                      <CheckCircle style={{ color: '#4caf50' }} />
                    ) : (
                      <Error style={{ color: '#f44336' }} />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default MigrationTool; 